/**
 * Translation loader for both server and client environments
 */
import { type AvailableLanguage } from './i18n-store';

// Cache for loaded translations
const translationCache = new Map<string, Record<string, string>>();

/**
 * Load translations for a specific language
 * Works on both server (Node.js) and client (browser)
 */
export async function loadTranslations(
    language: AvailableLanguage,
): Promise<Record<string, string>> {
    // Check cache first
    if (translationCache.has(language)) {
        return translationCache.get(language)!;
    }

    try {
        let translations: Array<{ key: string; value: string }> = [];

        if (typeof window === 'undefined') {
            // Server-side: Use Node.js fs to read from app/i18n folder
            const fs = await import('fs');
            const path = await import('path');
            const filePath = path.join(
                process.cwd(),
                'app',
                'i18n',
                `${language}.i18n.json`,
            );

            if (fs.existsSync(filePath)) {
                const content = fs.readFileSync(filePath, 'utf-8');
                translations = JSON.parse(content);
            }
        } else {
            // Client-side: Dynamic import of JSON files
            try {
                switch (language) {
                    case 'ru':
                        translations = (await import('~/i18n/ru.i18n.json'))
                            .default;
                        break;
                    case 'fr':
                        translations = (
                            await import('~/i18n/fr.i18n.json')
                        ).default;
                        break;
                    case 'de':
                        translations = (
                            await import('~/i18n/de.i18n.json')
                        ).default;
                        break;
                    case 'it':
                        translations = (
                            await import('~/i18n/it.i18n.json')
                        ).default;
                        break;
                    case 'es':
                        translations = (
                            await import('~/i18n/es.i18n.json')
                        ).default;
                        break;
                    case 'id':
                        translations = (
                            await import('~/i18n/id.i18n.json')
                        ).default;
                        break;
                    case 'ar':
                        translations = (
                            await import('~/i18n/ar.i18n.json')
                        ).default;
                        break;
                    case 'th':
                        translations = (
                            await import('~/i18n/th.i18n.json')
                        ).default;
                        break;
                    case 'vi':
                        translations = (
                            await import('~/i18n/vi.i18n.json')
                        ).default;
                        break;
                    case 'tr':
                        translations = (
                            await import('~/i18n/tr.i18n.json')
                        ).default;
                        break;
                    case 'nl':
                        translations = (
                            await import('~/i18n/nl.i18n.json')
                        ).default;
                        break;
                    case 'pl':
                        translations = (
                            await import('~/i18n/pl.i18n.json')
                        ).default;
                        break;
                    case 'ko':
                        translations = (
                            await import('~/i18n/ko.i18n.json')
                        ).default;
                        break;
                    case 'hi':
                        translations = (
                            await import('~/i18n/hi.i18n.json')
                        ).default;
                        break;
                    default:
                        translations = [];
                }
            } catch (importError) {
                // Import failed, use empty translations
                console.warn(`Failed to import translations for ${language}:`, importError);
                translations = [];
            }
        }

        // Convert array format to key-value object
        const translationMap: Record<string, string> = {};
        translations.forEach(({ key, value }) => {
            translationMap[key] = value;
        });

        // Cache the result
        translationCache.set(language, translationMap);
        return translationMap;
    } catch (error) {
        // Return empty object if loading fails
        const emptyTranslations = {};
        translationCache.set(language, emptyTranslations);
        return emptyTranslations;
    }
}

/**
 * Clear translation cache (useful for development/testing)
 */
export function clearTranslationCache(): void {
    translationCache.clear();
}
